//@ts-nocheck
import { FormProvider, useForm } from 'react-hook-form';
import { FolderKanban } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useCallback, useMemo } from 'react';
import { useDialogManager } from '@/hooks/useDialogManager';
import { useUniqueProject, useUpdateProject } from '@/hooks/api/useProjects';
import { FormInput } from '@/components/shared/FormInput';
import { DialogShell } from '@/components/shared/DialogShell';

const fmt = (iso?: string | null) =>
  iso ? new Date(iso).toISOString().split('T')[0] : '';

export const EditProjectForm = ({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}) => {
  const { closeDialog } = useDialogManager();
  const { updateProject, isUpdating } = useUpdateProject();
  const { data: project, isLoading } = useUniqueProject(id!);

  const defaultValues = useMemo(
    () =>
      project
        ? {
            name: project.name,
            startDate: fmt(project.startDate),
            endDate: fmt(project.endDate),
            description: project.description,
            projectTypeId: project.projectTypeId,
          }
        : undefined,
    [project],
  );

  const methods = useForm({
    values: defaultValues,
    resetOptions: { keepDirtyValues: true },
  });
  const {
    handleSubmit,
    reset,
    register,
    formState: { isDirty },
  } = methods;

  const close = useCallback(() => {
    reset();
    closeDialog();
  }, [reset, closeDialog]);

  const onSubmit = (data: any) => {
    updateProject(
      {
        name: data.name,
        description: data.description,
        startDate: new Date(data.startDate).toISOString(),
        endDate: new Date(data.endDate).toISOString(),
        projectTypeId: data.projectTypeId,
        projectId: project?.id,
      },
      {
        onSuccess: () => {
          close();
          toast.success('Project updated.');
        },
        onError: () => toast.error('Could not update project.'),
      },
    );
  };

  if (isLoading)
    return (
      <div className="flex items-center justify-center h-40 text-sm text-muted-foreground">
        Loading…
      </div>
    );

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col min-h-0">
        <DialogShell
          icon={<FolderKanban className="w-4 h-4 text-primary" />}
          title={project?.projectNumber ?? 'Edit Project'}
          description="Update project name or description."
          onCancel={close}
          submitText="Save Changes"
          isSubmitting={isUpdating}
          submitDisabled={!isDirty || isUpdating}
        >
          <FormInput
            name="name"
            label="Project Name"
            rules={{ required: 'Required' }}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormInput
              name="startDate"
              type="date"
              label="Start Date"
              disabled
            />
            <FormInput name="endDate" type="date" label="End Date" disabled />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description" className="text-sm font-medium">
              Description
            </Label>
            <Textarea
              {...register('description')}
              id="description"
              placeholder="Project goals, scope, or notes…"
              className="min-h-[100px] resize-none text-sm"
            />
          </div>
        </DialogShell>
      </form>
    </FormProvider>
  );
};
